import { isWeekend } from "date-fns";
import { call, select } from "redux-saga/effects";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Deal } from "@mapmycustomers/shared/types/entity";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import ClosedStageDealCountCardData from "@app/scene/dashboard/store/cards/closedStageDealCount/ClosedStageDealCountCardData";
import { callApi } from "@app/store/api/callApi";
import { getFunnelStages } from "@app/store/deal";
import { getOrganizationId } from "@app/store/iam";
import ClosedStageDealCountCardConfiguration from "@app/types/dashboard/cards/closedStageDealCount/ClosedStageDealCountCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { DateAggregationItem, granularityByDateRange } from "./common";

export function* fetchClosedStageDealCountCardDataHelper(params: {
  callback: (data: ClosedStageDealCountCardData) => void;
  configuration: ClosedStageDealCountCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const granularity = granularityByDateRange[dateRange.range];
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const filters = {
    closingDate: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
    stageId: { $in: criteria.dealStageIds },
    teamId: isTeamScope ? { [PlatformFilterOperator.CONTAINS]: [scope.teamId!] } : undefined,
    userId: scope?.userId ? { [PlatformFilterOperator.CONTAINS]: [scope.userId] } : undefined,
  };

  const payload = {
    $aggs: preferences.showChart
      ? {
          count: ["id"],
          primaryGroup: {
            field: "closingDate",
            interval: granularity,
            offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
            timezone: getLocalTimeZone(),
          },
        }
      : undefined,
    $filters: filters,
    $limit: 0,
  };

  const response: AggregatedListResponse<Deal, DateAggregationItem[] | undefined> = yield callApi(
    "fetchDeals",
    orgId,
    payload
  );

  let sum = 0;
  const result: ClosedStageDealCountCardData = {
    count: response.total,
    history: response.aggregations?.map((item) => {
      sum += item.doc_count;
      return {
        count: sum,
        date: parseApiDate(item.key),
      };
    }),
  };

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const payload = {
      // we need to get previous period's chart data to show it when user hovers over the trend text
      $aggs: preferences.showChart
        ? {
            count: ["id"],
            primaryGroup: {
              field: "closingDate",
              interval: granularity,
              offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
              timezone: getLocalTimeZone(),
            },
          }
        : undefined,
      $filters: {
        ...filters,
        closingDate: {
          $gte: previousRange.startDate.toISOString(),
          $lte: previousRange.endDate.toISOString(),
        },
      },
      $limit: 0,
    };
    const response: AggregatedListResponse<Deal, DateAggregationItem[] | undefined> = yield callApi(
      "fetchDeals",
      orgId,
      payload
    );
    result.previousCount = response.total;
    if (preferences.showChart) {
      let sum = 0;
      result.previousHistory = response.aggregations?.map((item) => {
        sum += item.doc_count;
        return {
          count: sum,
          date: parseApiDate(item.key),
        };
      });
    }
  }

  const filteredHistory = result.history?.filter(
    ({ count, date }, index) =>
      !isWeekend(date) || count || (result.previousHistory && result.previousHistory[index]?.count)
  );

  const filteredPreviousHistory = result.previousHistory?.filter(
    ({ count }, index) =>
      count ||
      (result.history && (result.history[index]?.count || !isWeekend(result.history[index]?.date)))
  );

  result.history = filteredHistory;
  result.previousHistory = filteredPreviousHistory;
  const funnelStages: Record<number, Stage[]> = yield select(getFunnelStages);
  const stages = Object.values(funnelStages).flat();

  const metricTypes = [];
  const dealClosedStages = stages.filter(({ id }) => criteria.dealStageIds.includes(id));

  const hasClosedWon = dealClosedStages.some(({ type }) => type === DealStageType.WON);
  if (hasClosedWon) {
    metricTypes.push(LeaderboardMetricFieldName.DEALS_CLOSED_WON);
  }

  const hasClosedLost = dealClosedStages.some(({ type }) => type === DealStageType.LOST);
  if (hasClosedLost) {
    metricTypes.push(LeaderboardMetricFieldName.DEALS_CLOSED_LOST);
  }

  // if we should show the rank and scope is not the Entire Org
  if (preferences.showRankPosition && (scope?.teamId || scope?.userId) && metricTypes.length) {
    const response: ListResponse<LeaderboardItem> = yield callApi(
      "fetchLeaderboardData",
      orgId,
      {
        $and: [{ stageId: { $in: criteria.dealStageIds } }],
        date: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() },
        includePerformanceMetric: true,
        property: {
          $in: metricTypes,
        },
        summary: isTeamScope ? "team" : "user",
      },
      undefined
    );
    const data = response.data.sort(
      (a, b) =>
        (b.values.dealsClosedLost ?? 0) +
        (b.values.dealsClosedWon ?? 0) -
        ((a.values.dealsClosedLost ?? 0) + (a.values.dealsClosedWon ?? 0))
    );

    const rank = isTeamScope
      ? data.findIndex(({ teamId }) => teamId === scope!.teamId)
      : data.findIndex(({ userId }) => userId === scope!.userId);
    result.rank = rank >= 0 ? rank + 1 : undefined;
  }

  yield call(callback, result);
}
